/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useEffect } from "react";
import { useLogger } from "@noom/hive-analytics";

const AnalyticPageMap = {
  newsFeed: "feed",
  explore: "explore",
  category: "communityCategories",
  communityFeed: "communityFeed",
  communityNew: "createCommunity",
  communityEdit: "editCommunity",
  communityPost: "communityPost",
  communityComment: "communityComment",
  userFeed: "userProfile",
  userEdit: "userProfileEdit",
  userPost: "userPost",
  userComment: "userComment",
  login: "login",
  notifications: "notifications",
  loginForm: "loginForm",
  settings: "settings",
};

export function useAnalyticEvents() {
  const { log } = useLogger();

  const events = useMemo(
    () => ({
      onPageView: (params: { page: keyof typeof AnalyticPageMap }) => {
        log("UserViewedCommunityScreen", {
          screen: AnalyticPageMap[params.page],
        });
      },

      onCommunityCreate: (params: {
        communityId: string;
        name: string;
        isPrivate: string;
      }) => {
        log("UserCreatedCommunity", params);
      },

      onCommunityClose: (params: { communityId: string }) => {
        log("UserClosedCommunity", params);
      },

      onCommunityJoin: (params: { communityId: string }) => {
        log("UserJoinedCommunity", params);
      },

      onCommunityLeave: (params: { communityId: string }) => {
        log("UserLeftCommunity", params);
      },

      onPostCreate: (params: { postId: string; source: string }) => {
        log("UserCreatedPost", params);
      },

      onPostImpression: (params: { postId: string; communityId: string }) => {
        log("PostImpression", params);
      },

      onLoginWithNoom: () => {
        log("UserClicksNoomLogin");
      },

      onLoginWithSSO: () => {
        log("UserClicksSSOLogin");
      },

      onClickNotificationBell: () => {
        log("UserTappedNotificationTrayBell");
      },

      onClickNotificationRecord: ({
        sourceId,
        sourceType,
        id,
      }: {
        sourceId: string;
        sourceType: string;
        id: string;
      }) => {
        // In v1 of the notification tray, we were logging this event with the targetXyz parameter names,
        //  so I'm leaving that same naming scheme for continuity.
        log("UserTappedNotificationTrayRecord", {
          targetId: sourceId,
          targetType: sourceType,
          targetGroup: id,
        });
      },

      onClickMarkAllNotifications: () => {
        log("UserTappedNotificationTrayMarkAllRead");
      },

      onCommentCreate: ({
        isReply,
        isDisabled,
      }: {
        isReply: boolean;
        isDisabled: boolean;
      }) => {
        log("UserCreatedComment", { isReply, isDisabled });
      },

      onCommentsToggled: ({
        target,
        value,
      }: {
        target: "post" | "community";
        value: "on" | "off";
      }) => {
        log("UserCreatedComment", { target, value });
      },
    }),
    [log]
  );

  return events;
}

export function usePageView(page: keyof typeof AnalyticPageMap) {
  const log = useAnalyticEvents();

  useEffect(() => {
    log.onPageView({ page });
  }, [log]);
}
